import { useDeferredValue, useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  foldCityText,
  matchCity,
  preloadCities,
  searchCities,
  splitCityTerm,
  type CityMatch,
  type CitySearch,
  type CitySearchResult,
} from '../lib/usCities'
import { keepOptionVisible } from './keepOptionVisible'

const VISIBLE_LIMIT = 10
const MIN_CHARS = 2

const emptyResult: CitySearchResult = { matches: [], total: 0 }

/**
 * An editable combobox for the City box of an address: suggestions assist, free text
 * stays valid, and picking "Springfield, IL" is the only thing that ever writes into
 * the State box (via onSelect — typing never does). Leaving the box with a place typed
 * whole, "Columbus, OH", is that same pick made in words, and is taken as one.
 *
 * The state can be typed after the city to narrow the list, and the form's own State
 * box puts its places first: ten suggestions cannot hold sixteen Columbuses, and the
 * list says so when more match than it shows (TRKC-103).
 *
 * Mirrors the BrokerField contract — label, input and error are siblings tied by id,
 * same class names — so it drops into a form without the form learning anything new.
 * The ARIA shape is the WAI-ARIA 1.2 editable combobox: ids come from useId, never
 * hard-coded, because a Loads screen will mount one of these for origin and another
 * for destination.
 */
export function CityTypeahead({
  name,
  label,
  value,
  onChange,
  onSelect,
  error,
  maxLength,
  required = false,
  preferredState,
  search = searchCities,
  preload = preloadCities,
}: {
  name: string
  label: string
  value: string
  onChange: (value: string) => void
  onSelect: (match: CityMatch) => void
  error?: string
  maxLength?: number
  required?: boolean
  /** What the form's State box holds: that state's places lead the list. */
  preferredState?: string
  /** Injectable in tests, so no test ever imports the real dataset chunk. */
  search?: CitySearch
  preload?: () => void
}) {
  const { t } = useTranslation()
  const listboxId = useId()
  const errorId = `${name}-error`

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<CitySearchResult>(emptyResult)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [hasFocus, setHasFocus] = useState(false)

  // Selecting a suggestion changes `value` like typing does; this keeps that one
  // change from immediately reopening the list over the choice just made.
  const skipSearchRef = useRef(false)

  const popupRef = useRef<HTMLDivElement | null>(null)

  // What the box — and the form's State box — say now, for an answer that lands after
  // either has moved on.
  const valueRef = useRef(value)
  const preferredStateRef = useRef(preferredState)
  useEffect(() => {
    valueRef.current = value
    preferredStateRef.current = preferredState
  })

  // Local filtering is fast, but the list re-rendering on every keystroke is not
  // urgent — deferring it keeps the input itself instant. No debounce: 300ms timers
  // are for network calls, and this provider is an in-memory index.
  const deferredValue = useDeferredValue(value)

  useEffect(() => {
    if (!hasFocus) {
      setIsOpen(false)
      setActiveIndex(null)
      return
    }

    if (skipSearchRef.current) {
      skipSearchRef.current = false
      return
    }

    if (deferredValue.trim().length < MIN_CHARS) {
      setIsOpen(false)
      setResult(emptyResult)
      setActiveIndex(null)
      return
    }

    let active = true
    setIsLoading(true)
    // The previous list stays on screen while the new search runs: replacing it
    // with a loading flash on every keystroke made the popup flicker and resize
    // (road-test finding). What makes that safe is the settling guard below —
    // while a search is in flight or the deferred value lags the box, keys and
    // clicks refuse to commit anything from the list still showing.
    // The highlight does not survive, though: a stale option left highlighted —
    // aria-activedescendant included — would promise an Enter the guard refuses.
    setActiveIndex(null)

    void search(deferredValue, VISIBLE_LIMIT, preferredState)
      .then((found) => {
        if (!active) {
          return
        }
        setResult(found)
        setActiveIndex(null)
        setIsLoading(false)
        setIsOpen(true)
      })
      .catch(() => {
        // The dataset could not load: the typeahead goes quiet and the box remains
        // an ordinary text field — assistance is never allowed to break the form.
        // Quiet includes empty: stale matches left behind would still be openable
        // and selectable with ArrowDown.
        if (active) {
          setIsLoading(false)
          setIsOpen(false)
          setResult(emptyResult)
          setActiveIndex(null)
        }
      })

    return () => {
      active = false
    }
  }, [deferredValue, hasFocus, search, preferredState])

  // While the open list is settling — a search in flight, or the deferred value
  // still lagging the box — there is nothing coherent to act on: arrows, Enter and
  // clicks refuse to commit anything from the previous query. (A fast typist's
  // Enter must not submit a half-filled form either; CI's slower runner caught
  // exactly that in E2E.)
  const isSettling = isOpen && (isLoading || deferredValue !== value)

  // More places match than the list holds: said under it, with how to narrow it.
  const more = result.total > result.matches.length

  // aria-activedescendant moves the highlight without moving DOM focus, so the
  // browser never scrolls on its own — and ten options overflow the popup's
  // max-height. The popup follows the highlight by itself: scrollIntoView is off
  // the table because it also scrolls every ancestor, page included, which made
  // the whole screen jump while arrowing (road-test finding).
  useEffect(() => {
    if (activeIndex === null) {
      return
    }
    const popup = popupRef.current
    const option = document.getElementById(`${listboxId}-option-${activeIndex}`)
    if (popup !== null && option !== null) {
      popup.scrollTop = keepOptionVisible(popup, option)
    }
  }, [activeIndex, listboxId])

  function optionId(index: number): string {
    return `${listboxId}-option-${index}`
  }

  function select(match: CityMatch) {
    skipSearchRef.current = true
    onSelect(match)
    setIsOpen(false)
    setActiveIndex(null)
  }

  // "Columbus, OH" typed whole and left without a pick: the dispatcher named one place,
  // so it is taken as the pick it is — the city in its box, the state in its own. Left
  // as typed, the city box would hold "Columbus, OH", which no map lookup finds.
  function completeTypedPlace(typed: string) {
    const split = splitCityTerm(typed)
    if (split === null) {
      return
    }
    const city = foldCityText(split.city)
    // The pick writes both boxes, so both are held to what they said when the box was left.
    const stateWhenLeft = preferredState

    void search(typed, VISIBLE_LIMIT)
      .then((found) => {
        // The whole name and the whole state: "Columbus, O" names no single place.
        const named = found.matches.filter(
          (match) => foldCityText(match.city) === city && match.state === split.state,
        )
        // Only while neither box has moved: an edit made since, to the city or to the
        // State box, wins (Copilot on #49).
        if (
          named.length === 1 &&
          valueRef.current === typed &&
          preferredStateRef.current === stateWhenLeft
        ) {
          onSelect(named[0]!)
        }
      })
      .catch(() => {
        // No dataset, no completion: the box keeps what was typed, like any free text.
      })
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (isSettling) {
        event.preventDefault()
        return
      }
      if (result.matches.length === 0) {
        return
      }
      event.preventDefault()
      setIsOpen(true)
      const last = result.matches.length - 1
      setActiveIndex((current) => {
        if (event.key === 'ArrowDown') {
          return current === null || current === last ? 0 : current + 1
        }
        return current === null || current === 0 ? last : current - 1
      })
    } else if (event.key === 'Enter') {
      if (isSettling) {
        event.preventDefault()
        return
      }
      // Only intercepted while an option is focused; otherwise Enter keeps its
      // ordinary form-submit meaning, like in every other field.
      if (isOpen && activeIndex !== null) {
        const match = result.matches[activeIndex]
        if (match !== undefined) {
          event.preventDefault()
          select(match)
        }
      }
    } else if (event.key === 'Escape') {
      if (isOpen) {
        event.preventDefault()
        setIsOpen(false)
        setActiveIndex(null)
      }
    } else if (event.key === 'Tab') {
      // Closes without selecting, per the APG pattern.
      setIsOpen(false)
      setActiveIndex(null)
    }
  }

  return (
    <div className="field">
      <label htmlFor={name}>{label}</label>
      <div className="typeahead">
        <input
          id={name}
          name={name}
          type="text"
          role="combobox"
          // Chrome may still offer its own address autofill over this; accepted.
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={
            isOpen && activeIndex !== null ? optionId(activeIndex) : undefined
          }
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => {
            setHasFocus(true)
            // The dataset chunk starts travelling on first focus, so it is warm by
            // the time two characters exist to search with.
            preload()
          }}
          onBlur={() => {
            setHasFocus(false)
            completeTypedPlace(value)
          }}
          onKeyDown={handleKeyDown}
          maxLength={maxLength}
          required={required}
          aria-invalid={error !== undefined}
          aria-describedby={error === undefined ? undefined : errorId}
        />
        {isOpen && (
          <div className="typeahead__popup" ref={popupRef}>
            <ul role="listbox" id={listboxId} className="typeahead__list">
              {result.matches.map((match, index) => (
                <li
                  key={`${match.city}|${match.state}`}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={
                    index === activeIndex
                      ? 'typeahead__option typeahead__option--active'
                      : 'typeahead__option'
                  }
                  // preventDefault on mousedown keeps the input focused, so blur
                  // cannot close the list before this click lands — the classic
                  // hand-rolled-combobox trap.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    // Same guard as the keyboard: a list mid-replacement must not
                    // hand out cities from the previous query.
                    if (!isSettling) {
                      select(match)
                    }
                  }}
                >
                  <HighlightedCity city={match.city} term={value} />
                  {`, ${match.state}`}
                </li>
              ))}
            </ul>
            {result.matches.length === 0 && (
              <p className="typeahead__empty">
                {isLoading ? t('typeahead.loading') : t('typeahead.noMatches')}
              </p>
            )}
            {more && <p className="typeahead__more">{t('typeahead.more')}</p>}
          </div>
        )}
      </div>
      {/* Always mounted: a live region that appears and disappears announces nothing. */}
      <span className="visually-hidden" aria-live="polite">
        {isOpen && !isLoading && result.total > 0
          ? `${t('typeahead.results', { count: result.total })}${more ? ` ${t('typeahead.more')}` : ''}`
          : ''}
      </span>
      {error !== undefined && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

/**
 * The matched part of the name in bold, mapped back onto the original spelling — the
 * city part only, when the state was typed after it.
 */
function HighlightedCity({ city, term }: { city: string; term: string }) {
  const split = splitCityTerm(term)
  const match = matchCity(city, term) ?? (split === null ? null : matchCity(city, split.city))
  if (match === null) {
    return <>{city}</>
  }

  return (
    <>
      {city.slice(0, match.index)}
      <strong>{city.slice(match.index, match.index + match.length)}</strong>
      {city.slice(match.index + match.length)}
    </>
  )
}
